import ButtonComponent from "@components/Theme/Button/Button"
import {motion} from "framer-motion"
import React, {useEffect, useRef, useState} from "react"

import styles from "./SlideProgressIndicators.module.scss"

interface SlideProgressIndicatorsProps {
	slideCount: number
	activeIndex: number
	autoDuration: number
	onChange: (newIndex: number) => void
}

const SlideProgressIndicators: React.FC<SlideProgressIndicatorsProps> = ({
	slideCount,
	activeIndex,
	autoDuration,
	onChange,
}) => {
	const [isPlaying, setIsPlaying] = useState(true)
	const [elapsed, setElapsed] = useState(0) // ms
	const requestRef = useRef<number>()
	const startTimeRef = useRef<number | null>(null)

	const animate = (timestamp: number) => {
		if (!startTimeRef.current) startTimeRef.current = timestamp
		const delta = timestamp - startTimeRef.current + elapsed

		if (delta >= autoDuration) {
			setElapsed(0)
			startTimeRef.current = null
			onChange((activeIndex + 1) % slideCount)
		} else {
			setElapsed(delta)
			requestRef.current = requestAnimationFrame(animate)
		}
	}

	useEffect(() => {
		if (isPlaying) {
			requestRef.current = requestAnimationFrame(animate)
		}

		return () => {
			if (requestRef.current) cancelAnimationFrame(requestRef.current)
			startTimeRef.current = null
		}
	}, [isPlaying, activeIndex])

	const progress = Math.min(elapsed / autoDuration, 1) * 100

	return (
		<div className={styles.wrapper}>
			<ButtonComponent
				className={styles.playPauseButton}
				buttonType={"text"}
				size={"small"}
				onClick={() => {
					setIsPlaying((prev) => !prev)
					if (!isPlaying) startTimeRef.current = null
				}}
			>
				<motion.div
					key={isPlaying ? "pause" : "play"}
					initial={{opacity: 0, scale: 0.8}}
					animate={{opacity: 1, scale: 1}}
					exit={{opacity: 0, scale: 0.8}}
					style={{
						display: "flex",
						alignItems: "center",
					}}
					transition={{duration: 0.2}}
				>
					{isPlaying ? (
						<svg
							viewBox={"0 0 32 32"}
							width={"32"}
							height={"32"}
							fill={"currentColor"}
						>
							<path
								d={"M10,5 H14 V27 H10 Z M18,5 H22 V27 H18 Z"}
							/>
						</svg>
					) : (
						<svg
							viewBox={"0 0 32 32"}
							width={"32"}
							height={"32"}
							fill={"currentColor"}
						>
							<path d={"M8,5 L26,16 L8,27 Z"} />
						</svg>
					)}
				</motion.div>
			</ButtonComponent>
			{Array.from({length: slideCount}).map((_, i) => (
				<div
					key={i}
					className={`${styles.lineWrapper} ${
						i === activeIndex ? styles.active : ""
					}`}
					onClick={() => {
						setElapsed(0)
						onChange(i)
					}}
				>
					{i === activeIndex && (
						<div
							className={styles.lineProgress}
							style={{width: `${progress}%`}}
						/>
					)}
				</div>
			))}
		</div>
	)
}

export default SlideProgressIndicators
